import { useCallback, useEffect, useMemo } from 'react';
import useSearchParams from './useSearchParams';
import useDebounce from './useDebounce';

export type Operator =
  | 'contains'
  | 'equals'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'notContains'
  | 'notEquals'
  | 'upstreamFrom'
  | 'downstreamFrom';

export type FilterItem = {
  property: string;
  operator: Operator;
  value: string[];
};

export type UrlFilters = Array<FilterItem>;

type Props = { urlKey?: string; initialFilters?: UrlFilters };

const useUrlFilters = ({ urlKey, initialFilters }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlFilters: UrlFilters | undefined = useMemo(() => {
    if (!urlKey) return undefined;

    const filterParams = searchParams.get(urlKey);

    if (filterParams) {
      return JSON.parse(filterParams);
    }
    return undefined;
  }, [searchParams, urlKey]);

  const urlFiltersDebounced = useDebounce(urlFilters, 800);

  const addFilter = useCallback(
    (property: string, operator: Operator, value: string[]) => {
      if (!urlKey) return;
      const updatedFilters = [
        ...(urlFilters || []),
        { property, operator, value },
      ];

      setSearchParams((prev) => {
        prev.set(urlKey, JSON.stringify(updatedFilters));
        return prev;
      });
    },
    [setSearchParams, urlFilters, urlKey],
  );

  const applyFilters = useCallback(
    (filters: FilterItem[]) => {
      if (!urlKey) return;

      setSearchParams((prev) => {
        prev.set(urlKey, JSON.stringify(filters));
        return prev;
      });
    },
    [setSearchParams, urlKey],
  );

  const clearAllFilters = useCallback(() => {
    if (!urlKey) return;

    setSearchParams((prev) => {
      prev.delete(urlKey);
      return prev;
    });
  }, [setSearchParams, urlKey]);

  const compareFilter = (a: Partial<FilterItem>, b: Partial<FilterItem>) =>
    a.property === b.property &&
    a.operator === b.operator &&
    (!a.value || !b.value || a.value.toString() === b.value.toString());

  const clearFilter = useCallback(
    (property: string, operator: Operator, value?: string[]) => {
      if (!urlFilters || !urlKey) return;

      const updatedFilters = urlFilters.filter(
        (item) => !compareFilter({ property, operator, value }, item),
      );

      if (updatedFilters.length === 0) {
        clearAllFilters();
        return;
      }

      setSearchParams((prev) => {
        prev.set(urlKey, JSON.stringify(updatedFilters));
        return prev;
      });
    },
    [clearAllFilters, setSearchParams, urlFilters, urlKey],
  );

  const updateFilter = useCallback(
    (
      property: string,
      operator: Operator,
      value: string[],
      newValue: string[],
      index?: number,
    ) => {
      if (!urlFilters || !urlKey) return;

      if (newValue.length === 0) {
        clearFilter(property, operator, value);
        return;
      }

      const updatedFilters = urlFilters.findLastIndex((item) =>
        compareFilter({ property, operator, value }, item),
      );

      urlFilters[index ?? updatedFilters] = {
        property,
        operator,
        value: newValue,
      };

      setSearchParams((prev) => {
        prev.set(urlKey, JSON.stringify(urlFilters));
        return prev;
      });
    },
    [clearFilter, setSearchParams, urlFilters, urlKey],
  );

  useEffect(() => {
    if (initialFilters && urlKey && !urlFilters) {
      setSearchParams((prev) => {
        prev.set(urlKey, JSON.stringify(initialFilters));
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    urlFilters,
    urlFiltersDebounced,
    addFilter,
    applyFilters,
    clearFilter,
    updateFilter,
    clearAllFilters,
  };
};

export default useUrlFilters;
